import type { NextPage } from 'next';
import ErrorPage from 'next/error';
import ArticleCard from 'src/components/_new/article/card';
import ContentContainer from 'src/components/_new/containers/content-container';
import { PageLayout } from 'src/components/layout/pageLayout.component';
import { strapiApiUrlImage } from 'src/config';
import ArticleMeta from '../../components/_new/article/meta';
import BackButton from '../../components/button/back-button';
import type { Article, ArticleResponse } from '../../types/article';
import type { DataWorldChartTypes } from '../../types/types';

export interface ArticleSlugProps {
  post: ArticleResponse;
  countries: DataWorldChartTypes[];
  similarArticles: Article[] | null;
}

export const ArticleSlug: NextPage<ArticleSlugProps> = ({
  post,
  countries,
  similarArticles,
}) => {
  if (!post?.attributes?.slug) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <PageLayout
      fullWidth
      title={post?.attributes?.title}
      description={
        post?.attributes?.short_text ? post?.attributes?.short_text : ''
      }
      countries={countries}
      containerClassName="min-w-full [&&]:pb-0"
    >
      <ContentContainer className="flex flex-col justify-self-center pb-0 pt-8 max-sm:pb-0 smallMobile:w-full smallMobile:gap-2 lg:max-w-[1062px] lg:gap-4">
        <BackButton url={`/blog`} />
        <div className="flex flex-col gap-6">
          <div className="flex flex-col smallMobile:gap-2 lg:gap-3">
            <p className="font-semibold text-gray-950 smallMobile:text-2xl smallMobile:leading-loose lg:text-[40px] lg:leading-[52px]">
              {post?.attributes?.title}
            </p>
            <p className="font-normal text-gray-600 smallMobile:text-sm smallMobile:leading-tight lg:text-lg lg:leading-7">
              {post?.attributes?.short_text}
            </p>
          </div>
          <ArticleMeta article={post.attributes}></ArticleMeta>
        </div>

        {post.attributes.image.data?.attributes.url && (
          <img
            src={`${strapiApiUrlImage}${post.attributes.image.data?.attributes.url}`}
            className="aspect-[16/9] h-auto w-full object-cover smallMobile:mt-6 smallMobile:rounded-lg lg:mt-10 lg:rounded-2xl"
            alt="Article"
          />
        )}

        <div className="flex gap-7 smallMobile:flex-col-reverse lg:mt-10 lg:flex-row">
          <div dangerouslySetInnerHTML={{ __html: post.attributes.content }} />
          {/* <div className="flex h-fit flex-col gap-4 rounded-xl bg-neutral-50 smallMobile:min-w-full smallMobile:p-4 lg:min-w-[300px] lg:p-6">
            <p className="text-sm font-semibold leading-tight text-gray-950">
              Contents
            </p>
          </div> */}
        </div>
      </ContentContainer>

      <ContentContainer className="flex flex-col gap-10 lg:w-full">
        <p className="text-[32px] font-semibold leading-10 text-gray-950">
          Similar articles
        </p>
        <div className="-m-4 flex gap-[26px] overflow-x-scroll p-4 [&::-webkit-scrollbar]:hidden">
          {similarArticles?.map((article) => {
            return (
              <ArticleCard
                className="grow basis-0 smallMobile:min-w-[300px] lg:min-w-min"
                key={article.slug}
                href={`/blog/${article.slug}`}
                article={article}
              />
            );
          })}
        </div>
      </ContentContainer>
    </PageLayout>
  );
};
